import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MODULE_CATALOG } from '@vce/shared';
import { X } from 'lucide-react';
import { api } from '@/lib/api';
import { cn, formatNumber } from '@/lib/utils';
import { useAuth } from '@/app/auth-context';
import { useTenant } from '@/app/tenant-context';

const DISMISS_KEY = 'vce.queueTipDismissed';

type DismissSnapshot = {
  pendingProducts: number;
  pendingSuggestions: number;
  approvedSuggestions: number;
};

function readDismissed(): DismissSnapshot | null {
  try {
    const raw = sessionStorage.getItem(DISMISS_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as DismissSnapshot;
  } catch {
    return null;
  }
}

function writeDismissed(snapshot: DismissSnapshot): void {
  sessionStorage.setItem(DISMISS_KEY, JSON.stringify(snapshot));
}

function clearDismissed(): void {
  sessionStorage.removeItem(DISMISS_KEY);
}

/**
 * Tip fixo no canto inferior direito: avisa fila de revisão e/ou escrita VTEX.
 * Reusa o mesmo poll de stats da nav; dispensável até os counts mudarem.
 */
export function QueueActionTip() {
  const { hasModule, can } = useAuth();
  const { tenantId } = useTenant();
  const location = useLocation();
  const [dismissed, setDismissed] = useState<DismissSnapshot | null>(() => readDismissed());

  const reviewEnabled = tenantId !== null && hasModule('review');
  const canWrite = hasModule('runs') && can('admin');

  const stats = useQuery({
    queryKey: ['suggestions', tenantId, 'stats'],
    queryFn: () => api.suggestions.stats(tenantId as string),
    enabled: reviewEnabled,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const pendingProducts = stats.data?.pendingProductCount ?? 0;
  const pendingSuggestions = stats.data?.byStatus?.pending ?? 0;
  const approvedSuggestions = stats.data?.byStatus?.approved ?? 0;

  const snapshot = useMemo(
    (): DismissSnapshot => ({
      pendingProducts,
      pendingSuggestions,
      approvedSuggestions,
    }),
    [pendingProducts, pendingSuggestions, approvedSuggestions],
  );

  useEffect(() => {
    if (!dismissed) return;
    if (
      dismissed.pendingProducts !== snapshot.pendingProducts ||
      dismissed.pendingSuggestions !== snapshot.pendingSuggestions ||
      dismissed.approvedSuggestions !== snapshot.approvedSuggestions
    ) {
      clearDismissed();
      setDismissed(null);
    }
  }, [dismissed, snapshot]);

  const onReviewRoute = location.pathname.startsWith(MODULE_CATALOG.review.path);
  const showPending = pendingProducts > 0 || pendingSuggestions > 0;
  const showWrite = canWrite && approvedSuggestions > 0;

  // Na Fila, só mantém o tip se houver escrita pendente (admin).
  const visible =
    reviewEnabled &&
    !dismissed &&
    (showWrite || (showPending && !onReviewRoute));

  if (!visible) return null;

  function dismiss(): void {
    writeDismissed(snapshot);
    setDismissed(snapshot);
  }

  return (
    <aside
      aria-label="Ações pendentes na fila"
      className={cn(
        'fixed bottom-4 right-4 z-40 w-[min(100%-2rem,20rem)]',
        'rounded-[12px] border border-[var(--color-hairline)] bg-[var(--color-surface-card)]',
        'p-3 shadow-[0_8px_30px_rgba(0,0,0,0.12)]',
      )}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1 space-y-2">
          {showPending && !onReviewRoute ? (
            <p className="text-sm text-[var(--color-ink)]">
              Há{' '}
              <span className="font-semibold tabular-nums">
                {formatNumber(pendingProducts)}
              </span>{' '}
              {pendingProducts === 1 ? 'produto' : 'produtos'}
              {' / '}
              <span className="font-semibold tabular-nums">
                {formatNumber(pendingSuggestions)}
              </span>{' '}
              {pendingSuggestions === 1 ? 'sugestão' : 'sugestões'} na fila de revisão.
            </p>
          ) : null}
          {showWrite ? (
            <p className="text-sm text-[var(--color-ink)]">
              Há{' '}
              <span className="font-semibold tabular-nums">
                {formatNumber(approvedSuggestions)}
              </span>{' '}
              {approvedSuggestions === 1 ? 'sugestão aprovada' : 'sugestões aprovadas'} prontas
              para escrever na VTEX.
            </p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            {showPending && !onReviewRoute ? (
              <Link
                to={MODULE_CATALOG.review.path}
                className={cn(
                  'inline-flex min-h-8 items-center rounded-[8px] px-2.5 text-xs font-semibold',
                  'bg-[var(--color-primary)] text-[var(--color-on-primary)]',
                  'hover:opacity-90',
                )}
              >
                Abrir fila
              </Link>
            ) : null}
            {showWrite ? (
              <Link
                to={MODULE_CATALOG.runs.path}
                className={cn(
                  'inline-flex min-h-8 items-center rounded-[8px] px-2.5 text-xs font-semibold',
                  'border border-[var(--color-hairline)] text-[var(--color-ink)]',
                  'hover:bg-[var(--color-surface-elevated)]',
                )}
              >
                Ir a execuções
              </Link>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          aria-label="Dispensar aviso"
          onClick={dismiss}
          className={cn(
            'inline-flex size-7 shrink-0 items-center justify-center rounded-[8px]',
            'text-[var(--color-muted)] hover:bg-[var(--color-surface-elevated)] hover:text-[var(--color-ink)]',
          )}
        >
          <X className="size-3.5" aria-hidden />
        </button>
      </div>
    </aside>
  );
}
